import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { getTranslatedString } from 'helpers/i18n';
import Form from 'components/ui/Form';
import Spacer from 'components/ui/Spacer';
import MultiSelect from '../../../NoticeMultiselect';
import styled from 'styled-components';
import { getUnits } from 'actions/landlordPropertiesActions';
import {
  getTemplate,
  getTemplateValue,
  isNoticeOfEntryNotice,
  isLegalNotice,
  selectedProperties,
  modes,
} from '../NoticeHelpers';

const FullWidthSelect = styled(MultiSelect)`
  width: 100%;
`;

const DeliveryInput = ({ values, errors, touched, setFieldValue, noticeTemplates, properties }) => {
  const { mode } = values;

  const handleChange = (delivery) => {
    const updateForSingleProperty = () => {
      let newMode = modes.SELECTING_PROPERTY_OPTIONS;
      const selectedProperty = selectedProperties(values, properties)[0];

      getUnits(selectedProperty.value, { for_multiselect_with_floor_riser: true });

      if (delivery === 'units_within_property') {
        newMode = modes.SELECTING_UNIT;
      } else if (delivery === 'floors_within_property') {
        newMode = modes.SELECTING_FLOORS;
      } else if (delivery === 'rows_within_property') {
        newMode = modes.SELECTING_ROWS;
      }
      setFieldValue('mode', newMode);
      setFieldValue('units', []);
      setFieldValue('cotenants', []);

      if (delivery === 'units_within_property' && selectedProperty.is_single_unit) {
        const unit = {
          value: selectedProperty.unit_id,
          label: `${selectedProperty.label} ${getTranslatedString(
            'landlordPortal.noticeAssignmentForm.unit'
          )}`,
        };
        setFieldValue('units', [unit]);
        if (isLegalNotice(values) || isNoticeOfEntryNotice(values)) {
          setFieldValue('unit_option', 'to_unit_cotenants');
          setFieldValue('mode', modes.SELECTING_UNIT_COTENANTS);
        }
      }
    };

    if (!(values.properties.length > 1)) {
      updateForSingleProperty();
    } else {
      setFieldValue('units', []);
      setFieldValue('cotenants', []);
    }

    setFieldValue('delivery', delivery);
  };

  const templateDelivery = useMemo(() => {
    return getTemplateValue(
      getTemplate(noticeTemplates, values.notice_template_id),
      'delivery',
      'any'
    );
  }, [values.notice_template_id, noticeTemplates]);

  const deliveryOptions = useMemo(() => {
    if (isLegalNotice(values) || isNoticeOfEntryNotice(values)) {
      return [
        {
          value: 'units_within_property',
          label: getTranslatedString('landlordPortal.noticeAssignmentForm.unitWithinProperty'),
        },
      ];
    }

    let propertyOptionFields = [];

    if (templateDelivery !== 'single_unit') {
      propertyOptionFields.push({
        value: 'directly_to_property',
        label: getTranslatedString('landlordPortal.noticeAssignmentForm.assignToProperty'),
      });
    }

    propertyOptionFields.push({
      value: 'units_within_property',
      label: getTranslatedString('landlordPortal.noticeAssignmentForm.unitWithinProperty'),
    });

    const selectedProperties = values.properties;

    // If selected_properties is empty or contains more than 1 property just return!!!
    if (!selectedProperties) return propertyOptionFields;
    if (Array.isArray(selectedProperties) && selectedProperties.length !== 1) {
      return propertyOptionFields;
    }

    let selectedPropertySingle = selectedProperties;
    if (Array.isArray(selectedProperties)) {
      selectedPropertySingle = selectedProperties[0];
    }

    const selectedProperty = properties.find(
      (property) => property.value === selectedPropertySingle
    );

    if (selectedProperty.has_floors && templateDelivery !== 'single_unit') {
      propertyOptionFields.push({
        value: 'floors_within_property',
        label: getTranslatedString('landlordPortal.noticeAssignmentForm.floorWithinProperty'),
      });
    }

    if (selectedProperty.has_risers && templateDelivery !== 'single_unit') {
      propertyOptionFields.push({
        value: 'rows_within_property',
        label: getTranslatedString('landlordPortal.noticeAssignmentForm.rowWithinProperty'),
      });
    }

    return propertyOptionFields;
  }, [values, properties, templateDelivery]);

  const disabled = useMemo(() => {
    return (
      // TODO - implement currentMode, readOnly, etc.
      templateDelivery === 'single_unit' || isLegalNotice(values) || isNoticeOfEntryNotice(values)
    );
  }, [values.notice_type, templateDelivery]);

  // Don't show delivery option if more than 1 property selected or we're still in
  // property selection mode.
  if (values.properties.length > 1 || mode === modes.SELECTING_PROPERTY) {
    return null;
  }

  return (
    <>
      <Spacer mb={1} />
      <Form.Item label={getTranslatedString('landlordPortal.noticeAssignmentForm.propertyOptions')}>
        <FullWidthSelect
          name="delivery"
          fullWidth
          value={values.delivery}
          onChange={handleChange}
          size="large"
          multiple={false}
          options={deliveryOptions}
          error={touched.delivery && errors.delivery}
          disabled={disabled}
        />
      </Form.Item>
    </>
  );
};

export default connect(
  (store) => ({
    properties: store.landlordProperties.properties,
    noticeTemplates: store.landlordNotices.notice_templates,
  }),
  { getUnits }
)(DeliveryInput);
